import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';
import { getDisbursementHistory, getReceiptHistory, createDisbursementRecord, createBankReceiptRecord, getBankReceiptProcessingRegisteredAmountInfo } from '../services/apiService';
import { FunnelIcon } from '@heroicons/react/24/outline';

const BankAccountManagement = () => {
  const navigate = useNavigate();

  const isBankdaTransaction = (item) => {
    const transactionNo = String(item?.거래번호 ?? '');
    return transactionNo.startsWith('BANKDA');
  };

  const getDisbursementRowKey = (item, idx) => {
    const transactionNo = String(item?.거래번호 ?? '');
    if (transactionNo) return transactionNo;
    const seq = String(item?.거래일련번호 ?? '');
    if (seq) return `SEQ:${seq}`;
    return `IDX:${idx}`;
  };

  const handleDisbursementRowToggle = async (item, idx) => {
    if (!item) return;
    const key = getDisbursementRowKey(item, idx);
    const nextExpanded = expandedDisbursementKey === key ? null : key;
    setExpandedDisbursementKey(nextExpanded);
    if (!nextExpanded) return;

    if (disbursementDetailByKey[key]) return;
    if (disbursementDetailLoadingKey === key) return;

    try {
      setDisbursementDetailLoadingKey(key);
      setDisbursementDetailErrorByKey((prev) => ({ ...prev, [key]: '' }));
      const transactionNo = String(item?.거래번호 ?? '');
      const details = await getBankReceiptProcessingRegisteredAmountInfo({
        type: '출금',
        transactionNo: transactionNo || null,
        transactionSeq: item?.거래일련번호 ?? null,
      });
      setDisbursementDetailByKey((prev) => ({ ...prev, [key]: Array.isArray(details) ? details : [] }));
    } catch (e) {
      setDisbursementDetailErrorByKey((prev) => ({ ...prev, [key]: '관련 정보를 불러오지 못했습니다.' }));
    } finally {
      setDisbursementDetailLoadingKey((prev) => (prev === key ? null : prev));
    }
  };

  const [activeTab, setActiveTab] = useState('accounts'); // 'accounts', 'disbursement', 'receipt'
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showLinkedOnly, setShowLinkedOnly] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [addForm, setAddForm] = useState({ 은행코드: '', 계좌번호: '', 예금주: '', 계좌별칭: '' });
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [linkStep, setLinkStep] = useState(1); // 1: 동의, 2: 입력
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [activeAccForLink, setActiveAccForLink] = useState(null);
  const [linkForm, setLinkForm] = useState({
    은행코드: '',
    계좌번호: '',
    예금주: '',
    계좌비밀번호: '',
    계좌별칭: '',
    계좌주구분: 'C', // C: 법인, P: 개인
    사업자번호1: '',
    사업자번호2: '',
    사업자번호3: '',
    주민번호1: '',
    주민번호2: '',
    fastLookupAgreed: false,
  });
  
  // 출납내역 상태
  const [disbursementData, setDisbursementData] = useState([]);
  const [disbursementLoading, setDisbursementLoading] = useState(false);
  const [expandedDisbursementKey, setExpandedDisbursementKey] = useState(null);
  const [disbursementDetailLoadingKey, setDisbursementDetailLoadingKey] = useState(null);
  const [disbursementDetailErrorByKey, setDisbursementDetailErrorByKey] = useState({});
  const [disbursementDetailByKey, setDisbursementDetailByKey] = useState({});
  const [disbursementStartDate, setDisbursementStartDate] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    return oneYearAgo.toISOString().split('T')[0];
  });
  const [disbursementEndDate, setDisbursementEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  // 수납내역 상태
  const [receiptData, setReceiptData] = useState([]);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [expandedReceiptKey, setExpandedReceiptKey] = useState(null);
  const [receiptDetailLoadingKey, setReceiptDetailLoadingKey] = useState(null);
  const [receiptDetailErrorByKey, setReceiptDetailErrorByKey] = useState({});
  const [receiptDetailByKey, setReceiptDetailByKey] = useState({});
  const [receiptStartDate, setReceiptStartDate] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    return oneYearAgo.toISOString().split('T')[0];
  });
  const [receiptEndDate, setReceiptEndDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // 출납 신규 등록 모달 상태
  const [isDisbursementAddOpen, setIsDisbursementAddOpen] = useState(false);
  const [disbursementForm, setDisbursementForm] = useState({
    paymentMethod: '무통장',
    date: '',
    amount: '',
    accountId: '',
    note: '',
    senderName: '',
  });
  const [disbursementEditSeq, setDisbursementEditSeq] = useState(null);

  // 수납 신규 등록 모달 상태
  const [isReceiptAddOpen, setIsReceiptAddOpen] = useState(false);
  const [receiptForm, setReceiptForm] = useState({
    paymentMethod: '무통장',
    date: '',
    amount: '',
    accountId: '',
    note: '',
    senderName: '',
  });
  const [receiptEditSeq, setReceiptEditSeq] = useState(null);

  const [openDisbursementHeaderFilterKey, setOpenDisbursementHeaderFilterKey] = useState(null);
  const [disbursementHeaderFilterSearch, setDisbursementHeaderFilterSearch] = useState({});
  const [disbursementHeaderFilterSelected, setDisbursementHeaderFilterSelected] = useState({});
  const disbursementHeaderFilterRootRef = useRef(null);
  const disbursementHeaderFilterAnchorElRef = useRef(null);
  const [disbursementHeaderFilterAnchorRect, setDisbursementHeaderFilterAnchorRect] = useState(null);

  const [openReceiptHeaderFilterKey, setOpenReceiptHeaderFilterKey] = useState(null);
  const [receiptHeaderFilterSearch, setReceiptHeaderFilterSearch] = useState({});
  const [receiptHeaderFilterSelected, setReceiptHeaderFilterSelected] = useState({});
  const receiptHeaderFilterRootRef = useRef(null);
  const receiptHeaderFilterAnchorElRef = useRef(null);
  const [receiptHeaderFilterAnchorRect, setReceiptHeaderFilterAnchorRect] = useState(null);

  const disbursementTableColumns = useMemo(
    () => [
      { key: '결제구분', label: '결제구분' },
      { key: '종류', label: '종류' },
      { key: '계좌카드번호', label: '계좌카드번호' },
      { key: '출납일', label: '출납일' },
      { key: '송금카드소유자', label: '송금카드소유자' },
      { key: '거래금액', label: '거래금액' },
      { key: '비고', label: '비고' },
    ],
    []
  );

  const receiptTableColumns = useMemo(
    () => [
      { key: '결제구분', label: '결제구분' },
      { key: '종류', label: '종류' },
      { key: '수납일', label: '수납일' },
      { key: '송금카드소유자', label: '송금카드소유자' },
      { key: '거래금액', label: '거래금액' },
      { key: '거래번호', label: '거래번호' },
      { key: '비고', label: '비고' },
    ],
    []
  );

  const getDisbursementCellDisplay = (row, key) => {
    if (!row) return '';
    if (key === '거래금액') {
      if (typeof row?.거래금액 === 'number') return row.거래금액.toLocaleString();
      return String(row?.거래금액 ?? '');
    }
    return String(row?.[key] ?? '');
  };

  const getReceiptCellDisplay = (row, key) => {
    if (!row) return '';
    if (key === '거래금액') {
      if (typeof row?.거래금액 === 'number') return row.거래금액.toLocaleString();
      return String(row?.거래금액 ?? '');
    }
    return String(row?.[key] ?? '');
  };

  useEffect(() => {
    if (!openDisbursementHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = disbursementHeaderFilterRootRef.current;
      const anchor = disbursementHeaderFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenDisbursementHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openDisbursementHeaderFilterKey]);

  useEffect(() => {
    if (!openDisbursementHeaderFilterKey) return;
    const updateRect = () => {
      const el = disbursementHeaderFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setDisbursementHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openDisbursementHeaderFilterKey]);

  useEffect(() => {
    if (!openReceiptHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = receiptHeaderFilterRootRef.current;
      const anchor = receiptHeaderFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenReceiptHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openReceiptHeaderFilterKey]);

  useEffect(() => {
    if (!openReceiptHeaderFilterKey) return;
    const updateRect = () => {
      const el = receiptHeaderFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setReceiptHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openReceiptHeaderFilterKey]);

  const disbursementColumnValueOptions = useMemo(() => {
    const arr = Array.isArray(disbursementData) ? disbursementData : [];
    const map = {};
    disbursementTableColumns.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getDisbursementCellDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [disbursementData, disbursementTableColumns]);

  const receiptColumnValueOptions = useMemo(() => {
    const arr = Array.isArray(receiptData) ? receiptData : [];
    const map = {};
    receiptTableColumns.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getReceiptCellDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [receiptData, receiptTableColumns]);

  const toggleDisbursementHeaderFilter = (key, anchorEl = null) => {
    setOpenDisbursementHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        disbursementHeaderFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setDisbursementHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setDisbursementHeaderFilterAnchorRect(null);
        }
      } else {
        disbursementHeaderFilterAnchorElRef.current = null;
        setDisbursementHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const toggleReceiptHeaderFilter = (key, anchorEl = null) => {
    setOpenReceiptHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        receiptHeaderFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setReceiptHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setReceiptHeaderFilterAnchorRect(null);
        }
      } else {
        receiptHeaderFilterAnchorElRef.current = null;
        setReceiptHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const onDisbursementHeaderFilterSearchChange = (key, value) => {
    setDisbursementHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const onReceiptHeaderFilterSearchChange = (key, value) => {
    setReceiptHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const isAllSelectedForDisbursementKey = (key) => {
    const opts = disbursementColumnValueOptions?.[key] || [];
    const selected = disbursementHeaderFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const isAllSelectedForReceiptKey = (key) => {
    const opts = receiptColumnValueOptions?.[key] || [];
    const selected = receiptHeaderFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const toggleAllForDisbursementKey = (key) => {
    const opts = disbursementColumnValueOptions?.[key] || [];
    setDisbursementHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleAllForReceiptKey = (key) => {
    const opts = receiptColumnValueOptions?.[key] || [];
    setReceiptHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleDisbursementOptionForKey = (key, option) => {
    setDisbursementHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const toggleReceiptOptionForKey = (key, option) => {
    setReceiptHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const disbursementHeaderFilteredRows = useMemo(() => {
    const activeKeys = Object.keys(disbursementHeaderFilterSelected || {}).filter(
      (k) => Array.isArray(disbursementHeaderFilterSelected?.[k]) && disbursementHeaderFilterSelected[k].length > 0
    );
    if (activeKeys.length === 0) return disbursementData;
    const selectedMap = Object.fromEntries(
      activeKeys.map((k) => [k, new Set((disbursementHeaderFilterSelected[k] || []).map((v) => String(v)))])
    );
    return (Array.isArray(disbursementData) ? disbursementData : []).filter((row) =>
      activeKeys.every((k) => {
        const display = String(getDisbursementCellDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  }, [disbursementData, disbursementHeaderFilterSelected]);

  const receiptHeaderFilteredRows = useMemo(() => {
    const activeKeys = Object.keys(receiptHeaderFilterSelected || {}).filter(
      (k) => Array.isArray(receiptHeaderFilterSelected?.[k]) && receiptHeaderFilterSelected[k].length > 0
    );
    if (activeKeys.length === 0) return receiptData;
    const selectedMap = Object.fromEntries(
      activeKeys.map((k) => [k, new Set((receiptHeaderFilterSelected[k] || []).map((v) => String(v)))])
    );
    return (Array.isArray(receiptData) ? receiptData : []).filter((row) =>
      activeKeys.every((k) => {
        const display = String(getReceiptCellDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  }, [receiptData, receiptHeaderFilterSelected]);

  const getReceiptRowKey = (item, idx) => {
    const transactionNo = String(item?.거래번호 ?? '');
    if (transactionNo) return transactionNo;
    const seq = String(item?.거래일련번호 ?? '');
    if (seq) return `SEQ:${seq}`;
    return `IDX:${idx}`;
  };

  const handleReceiptRowToggle = async (item, idx) => {
    if (!item) return;
    const key = getReceiptRowKey(item, idx);
    const nextExpanded = expandedReceiptKey === key ? null : key;
    setExpandedReceiptKey(nextExpanded);
    if (!nextExpanded) return;

    if (receiptDetailByKey[key]) return;
    if (receiptDetailLoadingKey === key) return;

    try {
      setReceiptDetailLoadingKey(key);
      setReceiptDetailErrorByKey((prev) => ({ ...prev, [key]: '' }));
      const transactionNo = String(item?.거래번호 ?? '');
      const details = await getBankReceiptProcessingRegisteredAmountInfo({
        type: '입금',
        transactionNo: transactionNo || null,
        transactionSeq: item?.거래일련번호 ?? null,
      });
      setReceiptDetailByKey((prev) => ({ ...prev, [key]: Array.isArray(details) ? details : [] }));
    } catch (e) {
      setReceiptDetailErrorByKey((prev) => ({ ...prev, [key]: '관련 정보를 불러오지 못했습니다.' }));
    } finally {
      setReceiptDetailLoadingKey((prev) => (prev === key ? null : prev));
    }
  };

  useEffect(() => {
    const fetchCompanyBaseInfo = async () => {
      try {
        setLoading(true);
        setError('');
        const { companyNo } = getUserInfo() || {};
        const payload = {
          Table1: [
            { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
          ],
        };
        const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        const list = Array.isArray(data?.table8) ? data.table8 : [];
        setAccounts(list);
      } catch (e) {
        setError('데이터를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyBaseInfo();
  }, []);

  // 출납내역 로드
  const loadDisbursementHistory = async () => {
    try {
      setDisbursementLoading(true);
      const data = await getDisbursementHistory({
        startDate: disbursementStartDate,
        endDate: disbursementEndDate,
      });
      setDisbursementData(data);
    } catch (e) {
      console.error('출납내역 로드 실패:', e);
      setDisbursementData([]);
    } finally {
      setDisbursementLoading(false);
    }
  };

  // 수납내역 로드
  const loadReceiptHistory = async () => {
    try {
      setReceiptLoading(true);
      const data = await getReceiptHistory({
        startDate: receiptStartDate,
        endDate: receiptEndDate,
      });
      setReceiptData(data);
    } catch (e) {
      console.error('수납내역 로드 실패:', e);
      setReceiptData([]);
    } finally {
      setReceiptLoading(false);
    }
  };

  const formatDateForInput = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const applyDisbursementQuickRange = async (months) => {
    const end = new Date();
    const start = new Date(end);
    if (months === 0) {
      setDisbursementStartDate(formatDateForInput(end));
      setDisbursementEndDate(formatDateForInput(end));
    } else {
      start.setMonth(start.getMonth() - months);
      setDisbursementStartDate(formatDateForInput(start));
      setDisbursementEndDate(formatDateForInput(end));
    }
    await loadDisbursementHistory();
  };

  const applyReceiptQuickRange = async (months) => {
    const end = new Date();
    const start = new Date(end);
    if (months === 0) {
      setReceiptStartDate(formatDateForInput(end));
      setReceiptEndDate(formatDateForInput(end));
    } else {
      start.setMonth(start.getMonth() - months);
      setReceiptStartDate(formatDateForInput(start));
      setReceiptEndDate(formatDateForInput(end));
    }
    await loadReceiptHistory();
  };

  const openDisbursementAdd = () => {
    const today = new Date();
    const todayStr = formatDateForInput(today);
    const firstAcc = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
    setDisbursementEditSeq(null);
    setDisbursementForm(prev => ({
      ...prev,
      paymentMethod: prev.paymentMethod || '무통장',
      date: todayStr,
      amount: '',
      accountId: firstAcc ? String(firstAcc.계좌일련번호 ?? '') : '',
      note: '',
      senderName: '',
    }));
    setIsDisbursementAddOpen(true);
  };

  const openDisbursementEdit = (item) => {
    if (!item) return;

    if (isBankdaTransaction(item)) {
      window.alert('BANKDA로 시작하는 거래번호는 편집할 수 없습니다.');
      return;
    }

    const rawDate = String(item?.출납일 ?? '');
    const dateOnly = rawDate.includes(' ')
      ? rawDate.split(' ')[0]
      : rawDate.substring(0, 10);

    let amountStr = '';
    if (typeof item?.거래금액 === 'number') {
      amountStr = String(item.거래금액);
    } else if (typeof item?.거래금액 === 'string') {
      amountStr = item.거래금액.replace(/,/g, '');
    }

    const matchedAcc = Array.isArray(accounts)
      ? accounts.find((acc) => {
          const accNo = String(acc?.계좌번호 ?? '').replace(/-/g, '');
          const histNo = String(item?.계좌카드번호 ?? '').replace(/-/g, '');
          return accNo && histNo && histNo.includes(accNo);
        })
      : null;

    setDisbursementForm({
      paymentMethod: item?.결제구분 || '무통장',
      date: dateOnly || '',
      amount: amountStr || '',
      accountId: matchedAcc ? String(matchedAcc.계좌일련번호 ?? '') : '',
      note: item?.비고 ?? '',
      senderName: item?.송금카드소유자 ?? '',
    });

    const seq = item?.거래일련번호 ?? null;
    setDisbursementEditSeq(seq ? String(seq) : null);
    setIsDisbursementAddOpen(true);
  };

  const closeDisbursementAdd = () => {
    setDisbursementEditSeq(null);
    setIsDisbursementAddOpen(false);
  };

  const handleDisbursementFormChange = (e) => {
    const { name, value } = e.target;
    setDisbursementForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDisbursementAccountChange = (e) => {
    const accountId = e.target.value;
    setDisbursementForm(prev => ({ ...prev, accountId }));
  };

  const submitDisbursementAdd = async () => {
    const { paymentMethod, date, amount, accountId, note, senderName } = disbursementForm;
    const trimmedAmount = String(amount || '').trim();
    if (!paymentMethod || !date || !trimmedAmount || !accountId) {
      window.alert('결제구분, 출납일, 거래금액, 계좌는 필수입니다.');
      return;
    }

    const numericAmount = Number(trimmedAmount.replace(/,/g, ''));
    if (!numericAmount || Number.isNaN(numericAmount)) {
      window.alert('거래금액을 올바르게 입력해주세요.');
      return;
    }

    const PAYMENT_METHOD_MAP = {
      무통장: '254001',
      현금: '254004',
      일반카드: '254005',
    };
    const paymentMethodCode = PAYMENT_METHOD_MAP[paymentMethod] || '254001';

    const account = (accounts || []).find((a) => String(a?.계좌일련번호 ?? '') === String(accountId));
    if (!account) {
      window.alert('선택한 계좌 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const mode = disbursementEditSeq ? 'U' : 'I';
      const transactionSeq = disbursementEditSeq || 'NULL';
      await createDisbursementRecord({
        transactionDate: date,
        amount: numericAmount,
        paymentMethodCode,
        accountNo: account.계좌번호,
        bankCode: account.은행,
        senderName,
        note,
        mode,
        transactionSeq,
      });
      window.alert('출납 내역이 저장되었습니다.');
      setDisbursementEditSeq(null);
      setIsDisbursementAddOpen(false);
      await loadDisbursementHistory();
    } catch (e) {
      window.alert('출납 내역 저장에 실패했습니다.');
    }
  };

  const openReceiptAdd = () => {
    const today = new Date();
    const todayStr = formatDateForInput(today);
    const firstAcc = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
    setReceiptEditSeq(null);
    setReceiptForm(prev => ({
      ...prev,
      paymentMethod: prev.paymentMethod || '무통장',
      date: todayStr,
      amount: '',
      accountId: firstAcc ? String(firstAcc.계좌일련번호 ?? '') : '',
      note: '',
      senderName: '',
    }));
    setIsReceiptAddOpen(true);
  };

  const openReceiptEdit = (item) => {
    if (!item) return;

    if (isBankdaTransaction(item)) {
      window.alert('BANKDA로 시작하는 거래번호는 편집할 수 없습니다.');
      return;
    }

    const rawDate = String(item?.수납일 ?? '');
    const dateOnly = rawDate.includes(' ')
      ? rawDate.split(' ')[0]
      : rawDate.substring(0, 10);

    let amountStr = '';
    if (typeof item?.거래금액 === 'number') {
      amountStr = String(item.거래금액);
    } else if (typeof item?.거래금액 === 'string') {
      amountStr = item.거래금액.replace(/,/g, '');
    }

    const matchedAcc = Array.isArray(accounts)
      ? accounts.find((acc) => {
          const accNo = String(acc?.계좌번호 ?? '').replace(/-/g, '');
          const histNo = String(item?.계좌카드번호 ?? item?.계좌번호 ?? '').replace(/-/g, '');
          return accNo && histNo && histNo.includes(accNo);
        })
      : null;

    setReceiptForm({
      paymentMethod: item?.결제구분 || '무통장',
      date: dateOnly || '',
      amount: amountStr || '',
      accountId: matchedAcc ? String(matchedAcc.계좌일련번호 ?? '') : '',
      note: item?.비고 ?? '',
      senderName: item?.송금카드소유자 ?? '',
    });

    const seq = item?.거래일련번호 ?? item?.거래번호 ?? null;
    setReceiptEditSeq(seq ? String(seq) : null);
    setIsReceiptAddOpen(true);
  };

  const closeReceiptAdd = () => {
    setReceiptEditSeq(null);
    setIsReceiptAddOpen(false);
  };

  const handleReceiptFormChange = (e) => {
    const { name, value } = e.target;
    setReceiptForm(prev => ({ ...prev, [name]: value }));
  };

  const handleReceiptAccountChange = (e) => {
    const accountId = e.target.value;
    setReceiptForm(prev => ({ ...prev, accountId }));
  };

  const submitReceiptAdd = async () => {
    const { paymentMethod, date, amount, accountId, note, senderName } = receiptForm;
    const trimmedAmount = String(amount || '').trim();
    if (!paymentMethod || !date || !trimmedAmount || !accountId) {
      window.alert('결제구분, 수납일, 거래금액, 계좌는 필수입니다.');
      return;
    }

    const numericAmount = Number(trimmedAmount.replace(/,/g, ''));
    if (!numericAmount || Number.isNaN(numericAmount)) {
      window.alert('거래금액을 올바르게 입력해주세요.');
      return;
    }

    const PAYMENT_METHOD_MAP = {
      무통장: '254001',
      현금: '254004',
      일반카드: '254005',
    };
    const paymentMethodCode = PAYMENT_METHOD_MAP[paymentMethod] || '254001';

    const account = (accounts || []).find((a) => String(a?.계좌일련번호 ?? '') === String(accountId));
    if (!account) {
      window.alert('선택한 계좌 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      const mode = receiptEditSeq ? 'U' : 'I';
      const transactionSeq = receiptEditSeq || 'NULL';
      await createBankReceiptRecord({
        transactionDate: date,
        amount: numericAmount,
        paymentMethodCode,
        accountNo: account.계좌번호,
        bankCode: account.은행,
        senderName,
        note,
        mode,
        transactionSeq,
      });
      window.alert('수납 내역이 저장되었습니다.');
      setReceiptEditSeq(null);
      setIsReceiptAddOpen(false);
      await loadReceiptHistory();
    } catch (e) {
      window.alert('수납 내역 저장에 실패했습니다.');
    }
  };

  // 탭 변경 시 데이터 로드
  useEffect(() => {
    if (activeTab === 'disbursement' && disbursementData.length === 0) {
      loadDisbursementHistory();
    } else if (activeTab === 'receipt' && receiptData.length === 0) {
      loadReceiptHistory();
    }
  }, [activeTab]);

  const filtered = useMemo(() => {
    let items = accounts;
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(a =>
        String(a?.은행명 || '').toLowerCase().includes(q) ||
        String(a?.계좌번호 || '').toLowerCase().includes(q) ||
        String(a?.계좌별칭 || '').toLowerCase().includes(q)
      );
    }
    if (showLinkedOnly) {
      items = items.filter(a => !!a?.연동여부);
    }
    return items;
  }, [accounts, search, showLinkedOnly]);

  const handleLinkToggle = async (acc) => {
    const linked = !!acc?.연동여부;
    if (!linked) {
      // Open link modal with prefilled values
      setActiveAccForLink(acc || null);
      setAgreementChecked(false);
      setLinkStep(1);
      // Ensure bank options are loaded for selection
      try {
        const payload = { Table1: [ { 컬럼명: '@종류', 값: '256', 리스트값: null }, ], };
        const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%BD%94%EB%93%9C';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data?.table) ? data.table : [];
          setBankOptions(list.map(x => ({ code: x?.코드 ?? '', name: x?.코드명 ?? '' })));
        }
      } catch (_) {
        // ignore
      }
      setLinkForm(prev => ({
        ...prev,
        은행코드: String(acc?.은행 ?? ''),
        계좌번호: String(acc?.계좌번호 ?? ''),
        예금주: String(acc?.예금주 ?? ''),
        계좌별칭: String(acc?.계좌별칭 ?? ''),
        계좌주구분: String(acc?.계좌주구분 ?? 'C') === 'P' ? 'P' : 'C',
      }));
      setIsLinkOpen(true);
      return;
    }
    try {
      const ok = window.confirm('해당 통장의 연동을 해지하시겠습니까?');
      if (!ok) return;
      const { companyNo, userNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
          { 컬럼명: '@유저번호', 값: String(userNo ?? ''), 리스트값: null },
          { 컬럼명: '@CRUD', 값: '계좌추가', 리스트값: null },
          { 컬럼명: '@계좌일련번호', 값: acc?.계좌일련번호, 리스트값: null },
          { 컬럼명: '@은행', 값: String(acc?.은행 ?? ''), 리스트값: null },
          { 컬럼명: '@계좌번호', 값: String(acc?.계좌번호 ?? ''), 리스트값: null },
          { 컬럼명: '@예금주', 값: String(acc?.예금주 ?? ''), 리스트값: null },
          { 컬럼명: '@연동여부', 값: '0', 리스트값: null },
          { 컬럼명: '@계좌주구분', 값: String(acc?.계좌주구분 ?? 'C'), 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%97%B0%EB%8F%99%EA%B3%84%EC%A2%8C_03';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (json && Object.keys(json).length === 0) {
        window.alert('연동이 해지되었습니다.');
      } else {
        window.alert('연동 해지 처리 결과를 확인하지 못했습니다.');
      }
      await reloadAccounts();
    } catch (e) {
      window.alert('연동 해지에 실패했습니다.');
    }
  };

  const openAdd = async () => {
    try {
      setIsAddOpen(true);
      setAddForm({ 은행코드: '', 계좌번호: '', 예금주: '', 계좌별칭: '' });
      const payload = {
        Table1: [
          { 컬럼명: '@종류', 값: '256', 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%EC%BD%94%EB%93%9C';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data?.table) ? data.table : [];
        setBankOptions(list.map(x => ({ code: x?.코드 ?? '', name: x?.코드명 ?? '' })));
      } else {
        setBankOptions([]);
      }
    } catch (_) {
      setBankOptions([]);
    }
  };

  const closeAdd = () => {
    setIsAddOpen(false);
  };

  const closeLink = () => {
    setIsLinkOpen(false);
    setActiveAccForLink(null);
    setLinkStep(1);
    setAgreementChecked(false);
    setLinkForm({
      은행코드: '', 계좌번호: '', 예금주: '', 계좌비밀번호: '', 계좌별칭: '', 계좌주구분: 'C',
      사업자번호1: '', 사업자번호2: '', 사업자번호3: '', 주민번호1: '', 주민번호2: '', fastLookupAgreed: false,
    });
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
  };

  const handleBankSelect = (e) => {
    const name = e.target.value;
    const found = bankOptions.find(b => b.name === name);
    setAddForm(prev => ({ ...prev, 은행코드: found?.code || '' }));
  };

  const handleLinkBankSelect = (e) => {
    const name = e.target.value;
    const found = bankOptions.find(b => b.name === name);
    setLinkForm(prev => ({ ...prev, 은행코드: found?.code || '' }));
  };

  const handleLinkChange = (e) => {
    const { name, value, type, checked } = e.target;
    setLinkForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const canSubmitLink = useMemo(() => {
    if (!linkForm.fastLookupAgreed) return false;
    if (!linkForm.은행코드 || !linkForm.계좌번호 || !linkForm.예금주 || !linkForm.계좌비밀번호) return false;
    if (linkForm.계좌주구분 === 'C') {
      return !!(linkForm.사업자번호1 && linkForm.사업자번호2 && linkForm.사업자번호3);
    } else {
      return !!(linkForm.주민번호1 && linkForm.주민번호2);
    }
  }, [linkForm]);

  const submitLink = async () => {
    if (!activeAccForLink) return;
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
          { 컬럼명: '@유저번호', 값: String(userNo ?? ''), 리스트값: null },
          { 컬럼명: '@CRUD', 값: '계좌추가', 리스트값: null },
          { 컬럼명: '@계좌일련번호', 값: String(activeAccForLink?.계좌일련번호 ?? ''), 리스트값: null },
          { 컬럼명: '@은행', 값: String(linkForm.은행코드 || ''), 리스트값: null },
          { 컬럼명: '@계좌번호', 값: String(linkForm.계좌번호 || ''), 리스트값: null },
          { 컬럼명: '@예금주', 값: String(linkForm.예금주 || ''), 리스트값: null },
          { 컬럼명: '@연동여부', 값: '1', 리스트값: null },
          { 컬럼명: '@계좌주구분', 값: String(linkForm.계좌주구분 || 'C'), 리스트값: null },
          { 컬럼명: '@주민번호1', 값: linkForm.계좌주구분 === 'P' ? String(linkForm.주민번호1 || '') : '      ', 리스트값: null },
          { 컬럼명: '@주민번호2', 값: linkForm.계좌주구분 === 'P' ? String(linkForm.주민번호2 || '') : '       ', 리스트값: null },
          { 컬럼명: '@사업자번호1', 값: linkForm.계좌주구분 === 'C' ? String(linkForm.사업자번호1 || '') : '', 리스트값: null },
          { 컬럼명: '@사업자번호2', 값: linkForm.계좌주구분 === 'C' ? String(linkForm.사업자번호2 || '') : '', 리스트값: null },
          { 컬럼명: '@사업자번호3', 값: linkForm.계좌주구분 === 'C' ? String(linkForm.사업자번호3 || '') : '', 리스트값: null },
          { 컬럼명: '@계좌비밀번호', 값: String(linkForm.계좌비밀번호 || ''), 리스트값: null },
          { 컬럼명: '@인터넷뱅킹ID', 값: '', 리스트값: null },
          { 컬럼명: '@인터넷뱅킹PW', 값: '', 리스트값: null },
          { 컬럼명: '@계좌별칭', 값: String(linkForm.계좌별칭 || ''), 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%97%B0%EB%8F%99%EA%B3%84%EC%A2%8C_03';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('failed');
      const json = await res.json().catch(() => ({}));
      const table = Array.isArray(json?.table) ? json.table : Array.isArray(json?.Table1) ? json.Table1 : [];
      const first = Array.isArray(table) && table.length > 0 ? table[0] : null;
      const status = first?.계좌상태 || first?.상태코드 || '';
      if (status === 'M' || status === 'OK') {
        window.alert('계좌 연동이 완료되었습니다.');
        await reloadAccounts();
        closeLink();
      } else {
        window.alert('연동 처리 결과를 확인하지 못했습니다. 다시 시도해주세요.');
      }
    } catch (e) {
      window.alert('연동 처리에 실패했습니다.');
    }
  };

  const reloadAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const { companyNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.RCT_S_%ED%9A%8C%EC%82%AC%EA%B8%B0%EB%B3%B8%EC%A0%95%EB%B3%B4';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const list = Array.isArray(data?.table8) ? data.table8 : [];
      setAccounts(list);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const saveAdd = async () => {
    try {
      const { companyNo, userNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
          { 컬럼명: '@유저번호', 값: String(userNo ?? ''), 리스트값: null },
          { 컬럼명: '@CRUD', 값: '계좌추가', 리스트값: null },
          { 컬럼명: '@계좌일련번호', 값: 'NULL', 리스트값: null },
          { 컬럼명: '@은행', 값: String(addForm.은행코드 || ''), 리스트값: null },
          { 컬럼명: '@계좌번호', 값: String(addForm.계좌번호 || ''), 리스트값: null },
          { 컬럼명: '@예금주', 값: String(addForm.예금주 || ''), 리스트값: null },
          { 컬럼명: '@연동여부', 값: '0', 리스트값: null },
          { 컬럼명: '@계좌주구분', 값: 'C', 리스트값: null },
          { 컬럼명: '@주민번호1', 값: '', 리스트값: null },
          { 컬럼명: '@주민번호2', 값: '', 리스트값: null },
          { 컬럼명: '@사업자번호1', 값: '', 리스트값: null },
          { 컬럼명: '@사업자번호2', 값: '', 리스트값: null },
          { 컬럼명: '@사업자번호3', 값: '', 리스트값: null },
          { 컬럼명: '@계좌비밀번호', 값: '', 리스트값: null },
          { 컬럼명: '@인터넷뱅킹ID', 값: '', 리스트값: null },
          { 컬럼명: '@인터넷뱅킹PW', 값: '', 리스트값: null },
          { 컬럼명: '@계좌별칭', 값: String(addForm.계좌별칭 || ''), 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.WSP_T_%EC%97%B0%EB%8F%99%EA%B3%84%EC%A2%8C_03';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (json && Object.keys(json).length === 0) {
        window.alert('추가 되었습니다.');
      }
      await reloadAccounts();
      setIsAddOpen(false);
    } catch (e) {
      window.alert('저장에 실패했습니다.');
    }
  };

  const deleteAccount = async (acc) => {
    try {
      if (!acc?.계좌일련번호) return;
      const ok = window.confirm('해당 통장 정보를 삭제하시겠습니까?');
      if (!ok) return;
      const { companyNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@업체번호', 값: String(companyNo ?? '1'), 리스트값: null },
          { 컬럼명: '@계좌일련번호', 값: String(acc.계좌일련번호), 리스트값: null },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.WSP_T_%EA%B3%84%EC%A2%8C%EC%82%AD%EC%A0%9C';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('failed');
      window.alert('삭제되었습니다.');
      await reloadAccounts();
    } catch (e) {
      window.alert('삭제에 실패했습니다.');
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h1 className="text-lg font-semibold">통장관리</h1>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setActiveTab('accounts')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'accounts'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          통장등록
        </button>
        <button
          onClick={() => setActiveTab('disbursement')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'disbursement'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          출납내역
        </button>
        <button
          onClick={() => setActiveTab('receipt')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'receipt'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          수납내역
        </button>
      </div>

      {/* 통장등록 탭 */}
      {activeTab === 'accounts' && (
        <>
          <div className="flex gap-2 items-center mb-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border rounded px-3 py-2 text-sm w-60"
              placeholder="은행/계좌번호/별칭 검색"
            />
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="h-4 w-4" checked={showLinkedOnly} onChange={(e) => setShowLinkedOnly(e.target.checked)} />
              연동된 계좌만 보기
            </label>
          </div>

          {loading && <div className="py-10 text-center text-gray-500">로딩 중...</div>}
          {!loading && error && <div className="py-10 text-center text-red-600">{error}</div>}

          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((acc) => {
            const key = String(acc?.계좌일련번호 ?? Math.random());
            const linked = !!acc?.연동여부;
            return (
              <div key={key} className="border border-gray-200 rounded-lg bg-white shadow-sm p-4 h-full">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-base font-semibold text-gray-900">{acc?.은행명 ?? ''}</div>
                  <span className={`text-xs px-2 py-0.5 rounded ${linked ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {linked ? '연동' : '미연동'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">계좌번호</span>
                    <span className="text-gray-900 font-medium">{acc?.계좌번호 ?? ''}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">예금주</span>
                    <span className="text-gray-900">{acc?.예금주 ?? ''}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">계좌별칭</span>
                    <span className="text-gray-900">{acc?.계좌별칭 ?? ''}</span>
                  </div>
                </div>
                {(acc?.acttag || acc?.act_status) && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2">
                    {acc?.acttag && (
                      <div className="text-xs font-medium text-amber-800">{acc.acttag}</div>
                    )}
                    {acc?.act_status && (
                      <div className="text-xs text-amber-700">{acc.act_status}</div>
                    )}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    className={`px-3 py-1.5 text-sm rounded ${linked ? 'bg-white border border-gray-300 hover:bg-gray-50' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                    onClick={() => handleLinkToggle(acc)}
                  >
                    {linked ? '연동해지' : '연동하기'}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-sm rounded bg-white-600 text-red-600"
                    onClick={() => deleteAccount(acc)}
                  >
                    삭제
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={openAdd}
            className="border-2 border-dashed border-gray-300 rounded-lg bg-white hover:bg-gray-50 p-4 flex items-center justify-center h-full text-gray-600"
          >
            통장 추가
          </button>
              {filtered.length === 0 && (
                <div className="col-span-full py-10 text-center text-gray-500">표시할 계좌가 없습니다.</div>
              )}
            </div>
          )}
        </>
      )}

      {/* 출납내역 탭 */}
      {activeTab === 'disbursement' && (
        <div className="bg-white rounded-lg border">
          <div className="px-4 pt-4 pb-2 border-b bg-gray-50 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600">검색기간</span>
              <input
              type="date"
              className="border rounded px-2 py-1 text-xs"
              value={disbursementStartDate}
              onChange={(e) => setDisbursementStartDate(e.target.value)}
            />
            <span className="text-xs text-gray-500">~</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-xs"
              value={disbursementEndDate}
              onChange={(e) => setDisbursementEndDate(e.target.value)}
            />
           
            <div className="flex items-center gap-1 ml-2 text-[11px]">
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyDisbursementQuickRange(0)}
              >
                오늘
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyDisbursementQuickRange(1)}
              >
                1개월
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyDisbursementQuickRange(3)}
              >
                3개월
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyDisbursementQuickRange(12)}
              >
                1년
              </button>
               <button
              type="button"
              onClick={loadDisbursementHistory}
              className="ml-2 px-3 py-1 text-xs rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              조회
            </button>
            </div>
            </div>
            <button
              type="button"
              onClick={openDisbursementAdd}
              className="px-3 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
            >
              신규 등록
            </button>
          </div>
          {disbursementLoading && <div className="py-10 text-center text-gray-500">로딩 중...</div>}
          {!disbursementLoading && disbursementData.length === 0 && (
            <div className="py-10 text-center text-gray-500">출납내역이 없습니다.</div>
          )}
          {!disbursementLoading && disbursementData.length > 0 && (
            <div className="overflow-x-auto">
              {openDisbursementHeaderFilterKey && (() => {
                const key = openDisbursementHeaderFilterKey;
                const widthPx = 224;
                const margin = 8;
                const rect = disbursementHeaderFilterAnchorRect;
                const leftBase = (rect?.left ?? 0) + (rect?.width ?? 0) / 2 - widthPx / 2;
                const topBase = (rect?.top ?? 0) + (rect?.height ?? 0) + 8;
                const maxLeft = Math.max(margin, (typeof window !== 'undefined' ? window.innerWidth : 0) - widthPx - margin);
                const left = Math.min(Math.max(margin, leftBase), maxLeft);

                return (
                  <div
                    ref={disbursementHeaderFilterRootRef}
                    style={{ position: 'fixed', top: topBase, left, width: widthPx }}
                    className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="검색..."
                        value={disbursementHeaderFilterSearch?.[key] ?? ''}
                        onChange={(e) => onDisbursementHeaderFilterSearchChange(key, e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                      />
                    </div>
                    <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
                      <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
                        <input type="checkbox" checked={isAllSelectedForDisbursementKey(key)} onChange={() => toggleAllForDisbursementKey(key)} />
                        <span>(모두 선택)</span>
                      </label>
                      <div className="my-1 border-t border-gray-100" />
                      {(disbursementColumnValueOptions?.[key] || [])
                        .filter((v) => {
                          const q = String(disbursementHeaderFilterSearch?.[key] ?? '').trim().toLowerCase();
                          if (!q) return true;
                          return String(v).toLowerCase().includes(q);
                        })
                        .map((v) => (
                          <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={(disbursementHeaderFilterSelected?.[key] || []).includes(v)}
                              onChange={() => toggleDisbursementOptionForKey(key, v)}
                            />
                            <span className="truncate">{String(v)}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                );
              })()}
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[100px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">결제구분</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['결제구분']) && disbursementHeaderFilterSelected['결제구분'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('결제구분', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[110px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">종류</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['종류']) && disbursementHeaderFilterSelected['종류'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('종류', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[160px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">계좌카드번호</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['계좌카드번호']) && disbursementHeaderFilterSelected['계좌카드번호'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('계좌카드번호', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">출납일</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['출납일']) && disbursementHeaderFilterSelected['출납일'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('출납일', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[150px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">송금카드소유자</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['송금카드소유자']) && disbursementHeaderFilterSelected['송금카드소유자'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('송금카드소유자', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">거래금액</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['거래금액']) && disbursementHeaderFilterSelected['거래금액'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('거래금액', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-normal break-words align-top max-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">비고</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(disbursementHeaderFilterSelected?.['비고']) && disbursementHeaderFilterSelected['비고'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDisbursementHeaderFilter('비고', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[70px]">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {disbursementHeaderFilteredRows.map((item, idx) => (
                    <React.Fragment key={getDisbursementRowKey(item, idx)}>
                      <tr
                        className={`hover:bg-gray-50 group cursor-pointer ${expandedDisbursementKey === getDisbursementRowKey(item, idx) ? 'bg-gray-50' : ''}`}
                        onClick={() => handleDisbursementRowToggle(item, idx)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap min-w-[100px]">{item?.결제구분 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[110px]">{item?.종류 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[160px]">{item?.계좌카드번호 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">{item?.출납일 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[150px]">{item?.송금카드소유자 ?? ''}</td>
                        <td className="px-4 py-3 text-left font-medium whitespace-nowrap min-w-[120px]">
                          {typeof item?.거래금액 === 'number' ? item.거래금액.toLocaleString() : item?.거래금액 ?? ''}
                        </td>
                        <td className="px-4 py-3 whitespace-normal break-words align-top max-w-[220px]">{item?.비고 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right align-top min-w-[70px]" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const disabled = isBankdaTransaction(item);
                            return (
                              <button
                                type="button"
                                disabled={disabled}
                                className={`px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 bg-white transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100'}`}
                                onClick={() => openDisbursementEdit(item)}
                              >
                                편집
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                      {expandedDisbursementKey === getDisbursementRowKey(item, idx) && (
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3" colSpan={8}>
                            {(() => {
                              const key = getDisbursementRowKey(item, idx);
                              const loading = disbursementDetailLoadingKey === key;
                              const errorMsg = disbursementDetailErrorByKey[key];
                              const details = disbursementDetailByKey[key];

                              if (loading) {
                                return <div className="text-xs text-gray-500">관련 정보를 불러오는 중...</div>;
                              }
                              if (errorMsg) {
                                return <div className="text-xs text-red-600">{errorMsg}</div>;
                              }
                              if (!Array.isArray(details) || details.length === 0) {
                                return <div className="text-xs text-gray-500">연결된 출금처리 정보가 없습니다.</div>;
                              }

                              return (
                                <div className="space-y-2">
                                  {details.map((d, i) => (
                                    <div
                                      key={i}
                                      role="button"
                                      tabIndex={0}
                                      className={`border border-gray-200 bg-white rounded p-3 ${d?.주문번호 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                      onClick={() => {
                                        const orderNo = d?.주문번호;
                                        if (!orderNo) return;
                                        const saleNo = d?.판매번호;
                                        const qs = saleNo ? `?saleNo=${encodeURIComponent(String(saleNo))}` : '';
                                        navigate(`/reservation-detail/${encodeURIComponent(String(orderNo))}${qs}`, saleNo ? { state: { saleNo } } : undefined);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;
                                        const orderNo = d?.주문번호;
                                        if (!orderNo) return;
                                        const saleNo = d?.판매번호;
                                        const qs = saleNo ? `?saleNo=${encodeURIComponent(String(saleNo))}` : '';
                                        navigate(`/reservation-detail/${encodeURIComponent(String(orderNo))}${qs}`, saleNo ? { state: { saleNo } } : undefined);
                                      }}
                                    >
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                        <div className="text-gray-700"><span className="text-gray-500">주문번호</span> {String(d?.주문번호 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">행사단체번호</span> {String(d?.행사단체번호 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">출발일</span> {String(d?.출발일 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">출금처리등록액</span> {typeof d?.입금처리등록액 === 'number' ? d.입금처리등록액.toLocaleString() : String(d?.입금처리등록액 ?? '')}</div>
                                      </div>
                                      <div className="mt-2 text-xs text-gray-800 font-medium">{String(d?.상품명 ?? '')}</div>
                                      {d?.내용 ? <div className="mt-1 text-[11px] text-gray-600">{String(d?.내용 ?? '')}</div> : null}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isReceiptAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeReceiptAdd} />
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
            <div className="px-5 py-3 border-b">
              <div className="text-base font-semibold">수납 내역 신규 등록</div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="mb-1 text-sm text-gray-600">결제구분</div>
                  <select
                    name="paymentMethod"
                    value={receiptForm.paymentMethod}
                    onChange={handleReceiptFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="무통장">무통장</option>
                    <option value="현금">현금</option>
                    <option value="일반카드">일반카드</option>
                  </select>
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">수납일</div>
                  <input
                    type="date"
                    name="date"
                    value={receiptForm.date}
                    onChange={handleReceiptFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">거래금액</div>
                  <input
                    type="number"
                    name="amount"
                    value={receiptForm.amount}
                    onChange={handleReceiptFormChange}
                    className="w-full border rounded px-3 py-2 text-sm text-right"
                    placeholder="0"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">종류(은행) / 계좌번호</div>
                  <select
                    value={receiptForm.accountId}
                    onChange={handleReceiptAccountChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">선택</option>
                    {Array.isArray(accounts) && accounts.map((acc) => (
                      <option key={String(acc.계좌일련번호 ?? acc.계좌번호)} value={String(acc.계좌일련번호 ?? '')}>
                        {`${acc.은행명 ?? ''} / ${acc.계좌번호 ?? ''}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">송금카드소유자</div>
                  <input
                    type="text"
                    name="senderName"
                    value={receiptForm.senderName}
                    onChange={handleReceiptFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">비고사항</div>
                  <input
                    type="text"
                    name="note"
                    value={receiptForm.note}
                    onChange={handleReceiptFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50"
                onClick={closeReceiptAdd}
              >
                취소
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded bg-primary-600 text-white hover:bg-primary-700"
                onClick={submitReceiptAdd}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수납내역 탭 */}
      {activeTab === 'receipt' && (
        <div className="bg-white rounded-lg border">
          <div className="px-4 pt-4 pb-2 border-b bg-gray-50 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-600">검색기간</span>
              <input
              type="date"
              className="border rounded px-2 py-1 text-xs"
              value={receiptStartDate}
              onChange={(e) => setReceiptStartDate(e.target.value)}
            />
            <span className="text-xs text-gray-500">~</span>
            <input
              type="date"
              className="border rounded px-2 py-1 text-xs"
              value={receiptEndDate}
              onChange={(e) => setReceiptEndDate(e.target.value)}
            />
           
            <div className="flex items-center gap-1 ml-2 text-[11px]">
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyReceiptQuickRange(0)}
              >
                오늘
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyReceiptQuickRange(1)}
              >
                1개월
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyReceiptQuickRange(3)}
              >
                3개월
              </button>
              <button
                type="button"
                className="px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50"
                onClick={() => applyReceiptQuickRange(12)}
              >
                1년
              </button>
               <button
              type="button"
              onClick={loadReceiptHistory}
              className="ml-2 px-3 py-1 text-xs rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              조회
            </button>
            </div>
            </div>
            <button
              type="button"
              onClick={openReceiptAdd}
              className="px-3 py-1 text-xs rounded bg-emerald-600 text-white hover:bg-emerald-700"
            >
              신규 등록
            </button>
          </div>
          {receiptLoading && <div className="py-10 text-center text-gray-500">로딩 중...</div>}
          {!receiptLoading && receiptData.length === 0 && (
            <div className="py-10 text-center text-gray-500">수납내역이 없습니다.</div>
          )}
          {!receiptLoading && receiptData.length > 0 && (
            <div className="overflow-x-auto">
              {openReceiptHeaderFilterKey && (() => {
                const key = openReceiptHeaderFilterKey;
                const widthPx = 224;
                const margin = 8;
                const rect = receiptHeaderFilterAnchorRect;
                const leftBase = (rect?.left ?? 0) + (rect?.width ?? 0) / 2 - widthPx / 2;
                const topBase = (rect?.top ?? 0) + (rect?.height ?? 0) + 8;
                const maxLeft = Math.max(margin, (typeof window !== 'undefined' ? window.innerWidth : 0) - widthPx - margin);
                const left = Math.min(Math.max(margin, leftBase), maxLeft);

                return (
                  <div
                    ref={receiptHeaderFilterRootRef}
                    style={{ position: 'fixed', top: topBase, left, width: widthPx }}
                    className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <input
                        type="text"
                        placeholder="검색..."
                        value={receiptHeaderFilterSearch?.[key] ?? ''}
                        onChange={(e) => onReceiptHeaderFilterSearchChange(key, e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                      />
                    </div>
                    <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
                      <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
                        <input type="checkbox" checked={isAllSelectedForReceiptKey(key)} onChange={() => toggleAllForReceiptKey(key)} />
                        <span>(모두 선택)</span>
                      </label>
                      <div className="my-1 border-t border-gray-100" />
                      {(receiptColumnValueOptions?.[key] || [])
                        .filter((v) => {
                          const q = String(receiptHeaderFilterSearch?.[key] ?? '').trim().toLowerCase();
                          if (!q) return true;
                          return String(v).toLowerCase().includes(q);
                        })
                        .map((v) => (
                          <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={(receiptHeaderFilterSelected?.[key] || []).includes(v)}
                              onChange={() => toggleReceiptOptionForKey(key, v)}
                            />
                            <span className="truncate">{String(v)}</span>
                          </label>
                        ))}
                    </div>
                  </div>
                );
              })()}
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[100px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">결제구분</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['결제구분']) && receiptHeaderFilterSelected['결제구분'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('결제구분', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[110px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">종류</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['종류']) && receiptHeaderFilterSelected['종류'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('종류', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">수납일</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['수납일']) && receiptHeaderFilterSelected['수납일'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('수납일', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[150px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">송금카드소유자</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['송금카드소유자']) && receiptHeaderFilterSelected['송금카드소유자'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('송금카드소유자', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">거래금액</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['거래금액']) && receiptHeaderFilterSelected['거래금액'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('거래금액', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[150px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">거래번호</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['거래번호']) && receiptHeaderFilterSelected['거래번호'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('거래번호', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-normal break-words align-top max-w-[220px]">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap">비고</span>
                        <button
                          type="button"
                          className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(receiptHeaderFilterSelected?.['비고']) && receiptHeaderFilterSelected['비고'].length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
                          title="필터"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleReceiptHeaderFilter('비고', e.currentTarget);
                          }}
                        >
                          <FunnelIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap min-w-[70px]">작업</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {receiptHeaderFilteredRows.map((item, idx) => (
                    <React.Fragment key={getReceiptRowKey(item, idx)}>
                      <tr
                        className={`hover:bg-gray-50 group cursor-pointer ${expandedReceiptKey === getReceiptRowKey(item, idx) ? 'bg-gray-50' : ''}`}
                        onClick={() => handleReceiptRowToggle(item, idx)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap min-w-[100px]">{item?.결제구분 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[110px]">{item?.종류 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">{item?.수납일 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[150px]">{item?.송금카드소유자 ?? ''}</td>
                        <td className="px-4 py-3 text-left font-medium whitespace-nowrap min-w-[120px]">
                          {typeof item?.거래금액 === 'number' ? item.거래금액.toLocaleString() : item?.거래금액 ?? ''}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap min-w-[150px]">{item?.거래번호 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-normal break-words align-top max-w-[220px]">{item?.비고 ?? ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-right align-top min-w-[70px]" onClick={(e) => e.stopPropagation()}>
                          {(() => {
                            const disabled = isBankdaTransaction(item);
                            return (
                              <button
                                type="button"
                                disabled={disabled}
                                className={`px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 bg-white transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100'}`}
                                onClick={() => openReceiptEdit(item)}
                              >
                                편집
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                      {expandedReceiptKey === getReceiptRowKey(item, idx) && (
                        <tr className="bg-gray-50">
                          <td className="px-4 py-3" colSpan={8}>
                            {(() => {
                              const key = getReceiptRowKey(item, idx);
                              const loading = receiptDetailLoadingKey === key;
                              const errorMsg = receiptDetailErrorByKey[key];
                              const details = receiptDetailByKey[key];

                              if (loading) {
                                return <div className="text-xs text-gray-500">관련 정보를 불러오는 중...</div>;
                              }
                              if (errorMsg) {
                                return <div className="text-xs text-red-600">{errorMsg}</div>;
                              }
                              if (!Array.isArray(details) || details.length === 0) {
                                return <div className="text-xs text-gray-500">연결된 입금처리 정보가 없습니다.</div>;
                              }

                              return (
                                <div className="space-y-2">
                                  {details.map((d, i) => (
                                    <div
                                      key={i}
                                      role="button"
                                      tabIndex={0}
                                      className={`border border-gray-200 bg-white rounded p-3 ${d?.주문번호 ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                                      onClick={() => {
                                        const orderNo = d?.주문번호;
                                        if (!orderNo) return;
                                        const saleNo = d?.판매번호;
                                        const qs = saleNo ? `?saleNo=${encodeURIComponent(String(saleNo))}` : '';
                                        navigate(`/reservation-detail/${encodeURIComponent(String(orderNo))}${qs}`, saleNo ? { state: { saleNo } } : undefined);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;
                                        const orderNo = d?.주문번호;
                                        if (!orderNo) return;
                                        const saleNo = d?.판매번호;
                                        const qs = saleNo ? `?saleNo=${encodeURIComponent(String(saleNo))}` : '';
                                        navigate(`/reservation-detail/${encodeURIComponent(String(orderNo))}${qs}`, saleNo ? { state: { saleNo } } : undefined);
                                      }}
                                    >
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                                        <div className="text-gray-700"><span className="text-gray-500">주문번호</span> {String(d?.주문번호 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">행사단체번호</span> {String(d?.행사단체번호 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">출발일</span> {String(d?.출발일 ?? '')}</div>
                                        <div className="text-gray-700"><span className="text-gray-500">입금처리등록액</span> {typeof d?.입금처리등록액 === 'number' ? d.입금처리등록액.toLocaleString() : String(d?.입금처리등록액 ?? '')}</div>
                                      </div>
                                      <div className="mt-2 text-xs text-gray-800 font-medium">{String(d?.상품명 ?? '')}</div>
                                      {d?.내용 ? <div className="mt-1 text-[11px] text-gray-600">{String(d?.내용 ?? '')}</div> : null}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isDisbursementAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeDisbursementAdd} />
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
            <div className="px-5 py-3 border-b">
              <div className="text-base font-semibold">출납 내역 신규 등록</div>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="mb-1 text-sm text-gray-600">결제구분</div>
                  <select
                    name="paymentMethod"
                    value={disbursementForm.paymentMethod}
                    onChange={handleDisbursementFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="무통장">무통장</option>
                    <option value="현금">현금</option>
                    <option value="일반카드">일반카드</option>
                  </select>
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">출납일</div>
                  <input
                    type="date"
                    name="date"
                    value={disbursementForm.date}
                    onChange={handleDisbursementFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">거래금액</div>
                  <input
                    type="number"
                    name="amount"
                    value={disbursementForm.amount}
                    onChange={handleDisbursementFormChange}
                    className="w-full border rounded px-3 py-2 text-sm text-right"
                    placeholder="0"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">종류(은행) / 계좌번호</div>
                  <select
                    value={disbursementForm.accountId}
                    onChange={handleDisbursementAccountChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">선택</option>
                    {Array.isArray(accounts) && accounts.map((acc) => (
                      <option key={String(acc.계좌일련번호 ?? acc.계좌번호)} value={String(acc.계좌일련번호 ?? '')}>
                        {`${acc.은행명 ?? ''} / ${acc.계좌번호 ?? ''}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">송금카드소유자</div>
                  <input
                    type="text"
                    name="senderName"
                    value={disbursementForm.senderName}
                    onChange={handleDisbursementFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <div className="mb-1 text-sm text-gray-600">비고사항</div>
                  <input
                    type="text"
                    name="note"
                    value={disbursementForm.note}
                    onChange={handleDisbursementFormChange}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50"
                onClick={closeDisbursementAdd}
              >
                취소
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm rounded bg-primary-600 text-white hover:bg-primary-700"
                onClick={submitDisbursementAdd}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeAdd} />
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
            <div className="px-5 py-3 border-b">
              <div className="text-base font-semibold">통장 추가</div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="mb-1 text-sm text-gray-600">은행</div>
                <select onChange={handleBankSelect} className="w-full border rounded px-3 py-2 text-sm" value={(() => {
                  const found = bankOptions.find(b => b.code === addForm.은행코드);
                  return found ? found.name : '';
                })()}>
                  <option value="">선택</option>
                  {bankOptions.map(b => (
                    <option key={`${b.code}-${b.name}`} value={b.name}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="mb-1 text-sm text-gray-600">계좌번호</div>
                <input name="계좌번호" value={addForm.계좌번호} onChange={handleAddChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="mb-1 text-sm text-gray-600">예금주</div>
                <input name="예금주" value={addForm.예금주} onChange={handleAddChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="mb-1 text-sm text-gray-600">계좌별칭</div>
                <input name="계좌별칭" value={addForm.계좌별칭} onChange={handleAddChange} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50" onClick={closeAdd}>취소</button>
              <button type="button" className="px-4 py-2 text-sm rounded bg-primary-600 text-white hover:bg-primary-700" onClick={saveAdd}>저장</button>
            </div>
          </div>
        </div>
      )}

      {isLinkOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeLink} />
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl mx-4">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div className="text-base font-semibold">계좌 연동하기</div>
              <div className="text-xs text-gray-500">Step {linkStep} / 2</div>
            </div>
            <div className="p-5">
              {linkStep === 1 && (
                <div className="space-y-4">
                  <div className="text-sm text-gray-700">다음 안내 사항을 확인해주세요.</div>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                    <li>연동계좌 등록 시 이용요금이 즉시 부과됩니다. 예치금잔액이 4,950원 이상 있으셔야 신청가능합니다.</li>
                    <li>계좌별 요금은 다음달 부터 매월 예치금에서 차감됩니다.(KRW 4,950원)</li>
                    <li>서비스 해지를 하실 경우 익월부터 적용됩니다.</li>
                    <li>신청 이후 거래내역부터 조회됩니다.(이전 거래내역은 수동입력하셔야 됩니다.)</li>
                    <li>은행별 인터넷뱅킹에서 별도의 설정을 사전에 해주셔야 합니다.</li>
                  </ul>
                  <label className="flex items-center gap-2 text-sm text-gray-800">
                    <input type="checkbox" className="h-4 w-4" checked={agreementChecked} onChange={(e)=>setAgreementChecked(e.target.checked)} />
                    위 안내에 동의합니다.
                  </label>
                </div>
              )}
              {linkStep === 2 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">은행</div>
                    <select onChange={handleLinkBankSelect} className="w-full border rounded px-3 py-2 text-sm" value={(() => {
                      const found = bankOptions.find(b => b.code === linkForm.은행코드);
                      return found ? found.name : '';
                    })()}>
                      <option value="">선택</option>
                      {bankOptions.map(b => (
                        <option key={`${b.code}-${b.name}`} value={b.name}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">계좌번호</div>
                    <input name="계좌번호" value={linkForm.계좌번호} onChange={handleLinkChange} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">예금주</div>
                    <input name="예금주" value={linkForm.예금주} onChange={handleLinkChange} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">계좌비밀번호</div>
                    <input name="계좌비밀번호" value={linkForm.계좌비밀번호} onChange={handleLinkChange} className="w-full border rounded px-3 py-2 text-sm" type="password" />
                  </div>
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">계좌별칭</div>
                    <input name="계좌별칭" value={linkForm.계좌별칭} onChange={handleLinkChange} className="w-full border rounded px-3 py-2 text-sm" />
                  </div>
                  <div className="col-span-1">
                    <div className="mb-1 text-sm text-gray-600">계좌구분</div>
                    <select name="계좌주구분" value={linkForm.계좌주구분} onChange={handleLinkChange} className="w-full border rounded px-3 py-2 text-sm">
                      <option value="C">법인</option>
                      <option value="P">개인</option>
                    </select>
                  </div>
                  {linkForm.계좌주구분 === 'C' ? (
                    <div className="col-span-2">
                      <div className="mb-1 text-sm text-gray-600">사업자 등록번호</div>
                      <div className="flex gap-2">
                        <input name="사업자번호1" value={linkForm.사업자번호1} onChange={handleLinkChange} className="w-20 border rounded px-3 py-2 text-sm" />
                        <input name="사업자번호2" value={linkForm.사업자번호2} onChange={handleLinkChange} className="w-16 border rounded px-3 py-2 text-sm" />
                        <input name="사업자번호3" value={linkForm.사업자번호3} onChange={handleLinkChange} className="w-24 border rounded px-3 py-2 text-sm" />
                      </div>
                    </div>
                  ) : (
                    <div className="col-span-2">
                      <div className="mb-1 text-sm text-gray-600">주민등록번호</div>
                      <div className="flex gap-2">
                        <input name="주민번호1" value={linkForm.주민번호1} onChange={handleLinkChange} className="w-28 border rounded px-3 py-2 text-sm" />
                        <input name="주민번호2" value={linkForm.주민번호2} onChange={handleLinkChange} className="w-32 border rounded px-3 py-2 text-sm" />
                      </div>
                    </div>
                  )}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-gray-800">
                        <input type="checkbox" name="fastLookupAgreed" className="h-4 w-4" checked={linkForm.fastLookupAgreed} onChange={handleLinkChange} />
                        인터넷뱅킹 빠른 계좌조회 등록 여부를 확인했습니다.
                      </label>
                      <button
                        type="button"
                        className="text-sm px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                        onClick={() => window.open('https://www.bankda.com/speed_bank/ViewBank.php', 'bankdaGuide', 'width=1000,height=800,noopener')}
                      >
                        절차 알아보기
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50" onClick={closeLink}>취소</button>
              {linkStep === 1 && (
                <button type="button" disabled={!agreementChecked} className={`px-4 py-2 text-sm rounded ${agreementChecked ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-200 text-gray-500'}`} onClick={()=>setLinkStep(2)}>다음</button>
              )}
              {linkStep === 2 && (
                <button type="button" disabled={!canSubmitLink} className={`px-4 py-2 text-sm rounded ${canSubmitLink ? 'bg-primary-600 text-white hover:bg-primary-700' : 'bg-gray-200 text-gray-500'}`} onClick={submitLink}>연동하기</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountManagement;
